<template>
  <teleport to="#modals">
    <vue-resizable
      data-cy="communication-tool-draggable"
      class="z-100"
      drag-selector=".draggable"
      :width="standaloneCallOptions.width"
      :height="standaloneCallOptions.height"
      :top="standaloneCallOptions.top"
      :left="standaloneCallOptions.left"
      :active="[]"
      @drag:end="updatePosition"
    >
      <div
        ref="draggable"
        class="bg-primary-1000 divide-y rounded-lg shadow-md"
      >
        <div class="p-4 draggable">
          <div class="flex items-start justify-between">
            <div class="space-y-1 text-primary-50">
              <span class="text-sm/5 font-semibold">
                {{ $t("QUICK_ACTIONS.MAKE_A_CALL") }}
              </span>
              <div class="flex items-center space-x-1 text-xs">
                <span>{{ $t("DIALER.OUTGOING_CALL_FROM") }}:</span>
                <span>{{ fromNumber }}</span>
              </div>
            </div>
            <icon-base
              :icon="IconClose"
              view-box="0 0 18 18"
              data-cy="close-quick-actions"
              class="cursor-pointer text-white/10 hover:text-white"
              @click="emit('close')"
            />
          </div>
        </div>
        <div class="p-4 flex items-center space-x-1">
          <icon-base
            :icon="IconDialerPhone"
            class="shrink-0 text-transparent"
            stroke="#868D9C"
          />
          <div class="flex-1">
            <lf-input
              name="standalone-phone-input"
              :value="phoneNumber"
              type="tel"
              :placeholder="$t('QUICK_ACTIONS.PHONE_NUMBER')"
              :show-label="false"
              class="mb-0! standalone-phone-input"
              data-cy="standalone-phone-input"
              @key-released="phoneNumber = $event"
            />
          </div>
          <button
            class="shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"
            data-cy="standalone-call-button"
            :class="{ invisible: !phoneNumber }"
            @click.stop="emit('call:start', phoneNumber)"
          >
            <icon-base
              :icon="IconDialerPhone"
              class="text-white ml-px"
              view-box="0 0 18 18"
            />
          </button>
        </div>
      </div>
    </vue-resizable>
  </teleport>
</template>
<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import VueResizable from "vue-resizable";

import { PhoneSelectionType } from "@/enums/communicationLogs";
import { DEFAULT_STANDALONE_CALL_OPTIONS } from "@/helpers/constants/deals";
import { useTwilio } from "@/hooks/communicationProviders";
import { useLocalStorageSetting } from "@/hooks/options";
import { useResizable } from "@/hooks/resizable";

import IconClose from "@/components/icons/IconClose.vue";
import IconDialerPhone from "@/components/icons/IconDialerPhone.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";

const emit = defineEmits<{ close: []; "call:start": [string] }>();

const standaloneCallOptions = useLocalStorageSetting(
  "standaloneCallOptions",
  DEFAULT_STANDALONE_CALL_OPTIONS
);

const draggable = useTemplateRef("draggable");

const { updatePosition } = useResizable(standaloneCallOptions, draggable);

const { savedPhoneNumber } = useTwilio();

const phoneNumber = ref("");

const fromNumber = computed(
  () => savedPhoneNumber.value?.phone_number ?? PhoneSelectionType.Auto
);
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.standalone-phone-input :deep(input) {
  @apply border-none bg-primary-1000! text-white focus:border-none focus:ring-0;
  caret-color: white;
}
.standalone-phone-input :deep(input:-webkit-autofill),
.standalone-phone-input :deep(input:-webkit-autofill:focus),
.standalone-phone-input :deep(input:-webkit-autofill:active),
.standalone-phone-input :deep(input:-webkit-autofill:hover) {
  box-shadow: 0 0 0 30px #272a40 inset !important;
  -webkit-box-shadow: 0 0 0 30px #272a40 inset !important;
  -webkit-text-fill-color: white !important;
  caret-color: white !important;
  border: none !important;
  outline: none !important;
}
</style>
